import { trayWalls } from "../CableTrayLayer/trayWalls";
import {
  HIGHLIGHT_BAND_WIDTH,
  HIGHLIGHT_EDGE_COLOR,
  HIGHLIGHT_EDGE_WIDTH,
  HIGHLIGHT_PULSE,
  HIGHLIGHT_TINT_OPACITY,
  VEIL_COLOR,
  VEIL_EXTENT,
  VEIL_MASK_ID,
  VEIL_OPACITY,
} from "./constants";
import type { CableTrayHighlightProps } from "./types";

export const CableTrayHighlight = ({ trays, trayType, strokeScale = 1 }: CableTrayHighlightProps) => {
  if (trayType === null) return null;
  const lit = trays.filter((tray) => tray.type === trayType);
  if (lit.length === 0) return null;

  const band = HIGHLIGHT_BAND_WIDTH * strokeScale;
  const edge = HIGHLIGHT_EDGE_WIDTH * strokeScale;
  const veilBox = { x: -VEIL_EXTENT, y: -VEIL_EXTENT, width: VEIL_EXTENT * 2, height: VEIL_EXTENT * 2 };

  return (
    // Above everything on purpose: the PDF's own symbols cannot be hidden, only faded.
    <g data-testid="cable-tray-highlight" className="pointer-events-none">
      <defs>
        <mask id={VEIL_MASK_ID} maskUnits="userSpaceOnUse" {...veilBox}>
          <rect {...veilBox} fill="white" />
          {lit.map((tray, index) => (
            <line
              key={index}
              x1={tray.from.x}
              y1={tray.from.y}
              x2={tray.to.x}
              y2={tray.to.y}
              stroke="black"
              strokeWidth={band}
              strokeLinecap="round"
            />
          ))}
        </mask>
      </defs>
      {/* A window is cut along the tray, so the drawing inside it stays at full strength. */}
      <rect data-testid="cable-tray-veil" {...veilBox} fill={VEIL_COLOR} fillOpacity={VEIL_OPACITY} mask={`url(#${VEIL_MASK_ID})`} />
      {lit.map((tray, index) => (
        <line
          key={`tint-${index}`}
          data-testid="cable-tray-highlight-band"
          x1={tray.from.x}
          y1={tray.from.y}
          x2={tray.to.x}
          y2={tray.to.y}
          stroke={HIGHLIGHT_EDGE_COLOR}
          strokeOpacity={HIGHLIGHT_TINT_OPACITY}
          strokeWidth={band}
          strokeLinecap="round"
        />
      ))}
      {lit.flatMap((tray, index) =>
        trayWalls({ from: tray.from, to: tray.to, halfWidth: band / 2 }).map((wall, side) => (
          <line
            key={`edge-${index}-${side}`}
            x1={wall.from.x}
            y1={wall.from.y}
            x2={wall.to.x}
            y2={wall.to.y}
            stroke={HIGHLIGHT_EDGE_COLOR}
            strokeWidth={edge}
            strokeLinecap="round"
          >
            <animate attributeName="stroke-opacity" values={HIGHLIGHT_PULSE} dur="1.6s" repeatCount="indefinite" />
          </line>
        )),
      )}
    </g>
  );
};
